import type { Condition, RecordingRecord } from './api'
import {
  RESEARCH_ONLY_SLOTS,
  SCORE_SLOTS,
  type DailyStatusResult,
  type DailyStatusView,
  type ScoreDisplay,
  type ScoreSlot,
} from './dailyStatus'
import { DEVICE_IDS } from './devices'
import { calendarDateParts } from './format'
import { CONDITION_OPTIONS, RECORDING_OUTCOME, bySessionDate } from './recordings'
import type { FanOutEntry } from './useFanOut'

/**
 * The trend, as data. Nothing in this file renders anything.
 *
 * ── WHY THE GEOMETRY LIVES HERE TOO ─────────────────────────────────────────
 *
 * Every rule §4.3 and §4.4 lay down is a rule about *data* — never connect
 * across a condition boundary, gaps stay gaps, encode confidence — so all of it
 * is testable with no DOM. Sprint 1.9.2 S4 adds the same rule one level out:
 * never connect across a HEADBAND boundary either, because each device keeps
 * its own baseline (a four-contact BrainBit day is scored against the classic
 * cohort, a Pro day against the Pro's), so a line from one to the other joins
 * two readings measured against different references. The coordinate mapping is here for a less obvious
 * reason: jsdom has no layout engine, so "x is proportional to elapsed time"
 * cannot be measured from a rendered chart. Computed here, it is a unit
 * assertion; computed in the component, it is a screenshot nobody takes.
 *
 * ── AND WHAT IS NOT HERE ────────────────────────────────────────────────────
 *
 * No `Math.min`/`Math.max` over a value, no `reduce` over values, no division by
 * `.length`, no smoothing, no slope, no threshold. Positioning a point is
 * presentation and is allowed; averaging two of them is a measurement nobody
 * took. `trend.test.ts` scans the text of this file for the vocabulary of that
 * mistake, which is cheap and is the first thing anybody would reach for.
 */

/** The seven a person may chart. `coupling_change` is research-only, so never. */
export const TREND_SLOTS: readonly ScoreSlot[] = SCORE_SLOTS.filter(
  (slot) => !RESEARCH_ONLY_SLOTS.has(slot),
)

/**
 * Signal quality, because the Status Guide puts it first — it "gates every other
 * reading" (`dailyStatus.ts:36`) — and because it is not one of the two slots
 * `SLOT_VALIDITY` can null out, so the screen never opens on an empty chart.
 * A trend in it is also a statement about the *recordings*, which is the one
 * thing a person can act on, rather than about them.
 */
export const DEFAULT_TREND_SLOT: ScoreSlot = 'signal_quality'

/**
 * The scale is the backend's and is used as given. **Fixed, and never fitted to
 * the data**: auto-fitting is the overclaim engine — three readings a tenth
 * apart fill the card top to bottom and read as a swing, and loading one more
 * page moves every point that was already on screen, so "it went up" becomes a
 * statement about the loader.
 */
export const TREND_DOMAIN = { min: 0, max: 100 } as const

export interface TrendPoint {
  recordingId: string
  condition: Condition
  /**
   * `RecordingRecord.device_id` — the headband the backend RESOLVED from the
   * archive. Verbatim, so an id this build cannot name still keeps its points
   * apart from every other headband's rather than joining them.
   */
  deviceId: string
  /** Verbatim `YYYY-MM-DD`. */
  sessionDate: string
  createdAt: string
  /** Verbatim. `formatScore` is still the only thing that makes it characters. */
  value: number
  /** The payload's own word for this value, or null. Never invented. */
  label: string | null
  /** From the read model, where an ABSENT flag already reads as LOW. */
  lowConfidence: boolean
  confidence: number | null
  /**
   * Calendar days since the previous point IN THIS SERIES — same condition,
   * same headband; null for the first. Never a gap across a device switch.
   */
  daysFromPrevious: number | null
  /** 0 = the domain's first day, 1 = its last. Proportional to ELAPSED DAYS. */
  x: number
  /** `value / 100`, and deliberately NOT clamped. */
  y: number
  /** Outside 0–100. Impossible today, and not silently absorbed. */
  outOfDomain: boolean
}

export interface TrendSeries {
  condition: Condition
  /** The headband every point here was recorded with. Two headbands are never one series. */
  deviceId: string
  points: readonly TrendPoint[]
}

export interface TrendSpan {
  firstDate: string
  lastDate: string
  /** Calendar days between them. Zero when everything is on one date. */
  days: number
}

/**
 * Where every eligible row went. The sum invariant is the point of this shape:
 * `rows === plotted + loading + failed + notScored + unreadable + noValue`, so a
 * bucket that silently swallows a row breaks one assertion rather than none.
 */
export interface TrendCoverage {
  rows: number
  plotted: number
  /** Fan-out `pending` or `loading`. */
  loading: number
  /** Fan-out `error`. */
  failed: number
  /** No result by construction — `empty`, `failed`, or a status we do not know. */
  notScored: number
  /** `readDailyStatus` refused it, or `session_date` was not a calendar date. */
  unreadable: number
  /** Readable, and THIS slot is null. Criterion 3, made countable. */
  noValue: number
}

export interface Trend {
  slot: ScoreSlot
  /**
   * One per condition AND headband: headbands in `DEVICE_IDS` order (an id
   * this build cannot name after them), and conditions in `CONDITION_OPTIONS`
   * order within each. A pair with no points is ABSENT, so no card can
   * advertise an empty series. With one headband this is exactly the list it
   * always was: at most two, EC then EO.
   */
  series: readonly TrendSeries[]
  /**
   * The loaded rows came from more than one headband, so every series must be
   * NAMED by its headband: two "Eyes closed" cards side by side are otherwise
   * indistinguishable. False for an account with one headband, which then sees
   * exactly the chart it always saw.
   */
  byDevice: boolean
  /** Every point, ascending. What the data list renders. */
  points: readonly TrendPoint[]
  /** The x domain, over PLOTTED points. Shared by both cards, so one calendar
   *  date sits at one x in each of them. */
  domain: TrendSpan | null
  /** The LOADED span, over eligible ROWS. What §4.5 asks the screen to state. */
  range: TrendSpan | null
  coverage: TrendCoverage
  /** The payload's own mark on this slot's scale, or null. Never a client constant. */
  baselineAt: number | null
  /**
   * The payload's own WORDS for that mark, or null. Agreed across every plotted
   * payload by the same rule as `baselineAt`, and independently: a chart whose
   * payloads agree on the position but not on the words names it with neither,
   * because picking between two labels is authoring one.
   */
  baselineLabel: string | null
}

/**
 * The payload's own mark on a slot's scale — and NOTHING if it does not carry
 * one (O2).
 *
 * There is no constant here and there is no default. Six of the eight slots are
 * scaled upstream so that the MIDDLE of the drawn axis is the undisturbed
 * value; `signal_quality` and `state_stability` are in `QUALITY_SCORES` and use
 * `SCALE_QUALITY` instead, where the TOP of the axis is the undisturbed value
 * and the middle means nothing at all. `signal_quality` is this chart's default
 * measure, so a client-drawn mid-scale rule would be asserting something false
 * on the first chart anyone opens.
 *
 * The key is the READ MODEL's `scaleBaseline`, not the wire's `scale_baseline`:
 * `dailyStatus.displayFor` rebuilds the display block from a fixed set of keys,
 * so the wire spelling never reaches here. Read structurally, so this compiles
 * and behaves whether or not that reader has landed yet.
 */
function scaleMarkLabelOf(display: ScoreDisplay | null): string | null {
  const words: unknown = (display as (ScoreDisplay & { scaleBaselineLabel?: unknown }) | null)
    ?.scaleBaselineLabel
  // `!== ''` rather than a length comparison: this module reads scores, and
  // `clientScience.test.ts` treats EVERY numeric comparison here as a site
  // needing a written reason. Not worth an allowlist entry -- and not worth
  // widening that file at all while Sprint 1.9.6 is editing its neighbour.
  return typeof words === 'string' && words !== '' ? words : null
}

function scaleMarkOf(display: ScoreDisplay | null): number | null {
  const at: unknown = (display as (ScoreDisplay & { scaleBaseline?: unknown }) | null)
    ?.scaleBaseline
  return typeof at === 'number' && Number.isFinite(at) ? at : null
}

const MS_PER_DAY = 86_400_000

/**
 * Days since the epoch for a `YYYY-MM-DD`, or null if it is not one.
 *
 * `Date.UTC` on parts from `calendarDateParts`, never `new Date(string)`. Both
 * operands of every subtraction are then built the same way, so the difference
 * is exact in every zone and across a DST boundary — which is the whole reason
 * that parser is exported rather than inlined. This suite runs in
 * `America/New_York`, where a local-Date implementation is wrong twice a year.
 */
export function dayNumber(sessionDate: string): number | null {
  const parts = calendarDateParts(sessionDate)
  if (parts === null) return null
  return Date.UTC(parts.year, parts.month - 1, parts.day) / MS_PER_DAY
}

const spanOf = (dates: readonly string[]): TrendSpan | null => {
  let first: string | null = null
  let last: string | null = null
  for (const date of dates) {
    if (first === null || date < first) first = date
    if (last === null || date > last) last = date
  }
  if (first === null || last === null) return null
  const a = dayNumber(first)
  const b = dayNumber(last)
  return { firstDate: first, lastDate: last, days: a === null || b === null ? 0 : b - a }
}

const readableOf = (entry: FanOutEntry<DailyStatusResult> | undefined): DailyStatusView | null =>
  entry?.status === 'ready' && entry.value !== undefined && entry.value.readable
    ? entry.value
    : null

export function buildTrend(
  rows: readonly RecordingRecord[],
  entries: Readonly<Record<string, FanOutEntry<DailyStatusResult> | undefined>>,
  slot: ScoreSlot,
): Trend {
  const coverage: TrendCoverage = {
    rows: 0,
    plotted: 0,
    loading: 0,
    failed: 0,
    notScored: 0,
    unreadable: 0,
    noValue: 0,
  }

  // A COPY. The list renders this same array newest-first, and sorting in place
  // would silently reverse the screen the caller is showing.
  const ordered = [...rows].sort(bySessionDate)
  const eligibleDates: string[] = []
  // Over the LOADED rows, not the plotted points, so the naming does not come
  // and go as the measure changes: an account that has recorded with two
  // headbands sees every card named, even on a measure only one of them has.
  let firstDevice: string | null = null
  let byDevice = false
  type Draft = Omit<TrendPoint, 'x' | 'daysFromPrevious'>
  const drafts: Draft[] = []
  /**
   * One entry per PLOTTED payload, so the chart can only draw a mark every
   * payload behind it agrees on. Two payloads that disagree, or one that does
   * not carry the field at all, put a second member in this set and the answer
   * is no mark — the client does not pick between them.
   */
  const scaleMarks = new Set<number | null>()
  /** The same agreement rule, over the words. See `Trend.baselineLabel`. */
  const scaleMarkLabels = new Set<string | null>()

  for (const row of ordered) {
    coverage.rows += 1
    if (firstDevice === null) firstDevice = row.device_id
    else if (row.device_id !== firstDevice) byDevice = true

    // Read, not restated: `hasResult` is the one copy of this four-value fact.
    // An `empty` recording is not a gap, not an error and not missing data — it
    // never had a result, and no lookup into `entries` happens for it.
    if (RECORDING_OUTCOME[row.status]?.hasResult !== true) {
      coverage.notScored += 1
      continue
    }
    eligibleDates.push(row.session_date)

    const entry = entries[row.recording_id]
    if (entry === undefined || entry.status === 'pending' || entry.status === 'loading') {
      coverage.loading += 1
      continue
    }
    if (entry.status === 'error') {
      coverage.failed += 1
      continue
    }
    const view = readableOf(entry)
    if (view === null || dayNumber(row.session_date) === null) {
      coverage.unreadable += 1
      continue
    }

    const card = view.scores.find((s) => s.slot === slot)
    // `null` is not zero, and it is not silence either: it is counted, so the
    // card can say how many recordings are missing from it and why.
    if (card === undefined || card.value === null) {
      coverage.noValue += 1
      continue
    }

    coverage.plotted += 1
    scaleMarks.add(scaleMarkOf(card.display))
    scaleMarkLabels.add(scaleMarkLabelOf(card.display))
    drafts.push({
      recordingId: row.recording_id,
      condition: row.condition,
      deviceId: row.device_id,
      sessionDate: row.session_date,
      createdAt: row.created_at,
      value: card.value,
      label: card.label,
      lowConfidence: view.lowConfidence,
      confidence: view.confidence,
      y: card.value / (TREND_DOMAIN.max - TREND_DOMAIN.min),
      outOfDomain: card.value < TREND_DOMAIN.min || card.value > TREND_DOMAIN.max,
    })
  }

  const domain = spanOf(drafts.map((d) => d.sessionDate))
  const first = domain === null ? null : dayNumber(domain.firstDate)

  const points: TrendPoint[] = drafts.map((d) => {
    const day = dayNumber(d.sessionDate)
    // A single date, or a single day's worth of dates, has no span to divide by.
    // Centring is the honest answer: there is no left-to-right to read.
    const x =
      domain === null || first === null || day === null || domain.days === 0
        ? 0.5
        : (day - first) / domain.days
    return { ...d, x, daysFromPrevious: null }
  })

  // The headbands the points came from: the named ones in `DEVICE_IDS` order,
  // then any id this build cannot name, in the order the points met them.
  const seen = [...new Set(points.map((p) => p.deviceId))]
  const devices = [
    ...DEVICE_IDS.filter((id) => seen.includes(id)),
    ...seen.filter((id) => !(DEVICE_IDS as readonly string[]).includes(id)),
  ]

  const series: TrendSeries[] = []
  for (const deviceId of devices) {
    for (const { value: condition } of CONDITION_OPTIONS) {
      const mine = points.filter((p) => p.condition === condition && p.deviceId === deviceId)
      if (mine.length === 0) continue
      // PER SERIES, so it is null at the start of each condition on each
      // headband and can never be a number that spans a condition boundary or
      // a device switch. Date arithmetic, not score arithmetic: it is a fact
      // about the record, not about the brain.
      let previous: number | null = null
      for (const point of mine) {
        const day = dayNumber(point.sessionDate)
        point.daysFromPrevious = previous === null || day === null ? null : day - previous
        if (day !== null) previous = day
      }
      series.push({ condition, deviceId, points: mine })
    }
  }

  const scaleMark = scaleMarks.size === 1 ? [...scaleMarks][0] : null
  // Gated on the POSITION as well: words with no mark to name would be a
  // sentence about a rule that is not drawn.
  const scaleMarkLabel =
    scaleMark === null || scaleMarkLabels.size !== 1 ? null : [...scaleMarkLabels][0]

  return {
    slot,
    series,
    byDevice,
    points,
    domain,
    range: spanOf(eligibleDates),
    coverage,
    baselineAt: scaleMark,
    baselineLabel: scaleMarkLabel,
  }
}

/**
 * The measures on offer, named by the payload's own `display.title`.
 *
 * Never by the slot key: `ScoreCard.tsx:41` refuses to render a card without a
 * display block for the same reason, and "activation_arousal" is not a thing to
 * put in front of a person. The list is the union over every readable payload
 * loaded so far, in `SCORE_SLOTS` order, so it only grows and never reorders
 * under a user mid-session.
 */
export function trendOptions(
  entries: Readonly<Record<string, FanOutEntry<DailyStatusResult> | undefined>>,
): readonly { slot: ScoreSlot; title: string }[] {
  const titles = new Map<ScoreSlot, string>()
  for (const entry of Object.values(entries)) {
    const view = readableOf(entry)
    if (view === null) continue
    for (const card of view.scores) {
      if (!TREND_SLOTS.includes(card.slot)) continue
      if (card.display !== null && !titles.has(card.slot)) {
        // `short_title ?? title` (ask B3). Several titles run past seventy
        // characters -- `alpha_relaxation`'s is eighty-one -- which a native
        // <select> truncates unpredictably per platform, and the same string is
        // embedded mid-clause in the coverage sentence beneath the chart.
        //
        // The fallback is what makes this safe to land before the field is
        // everywhere: a payload without a short form degrades to exactly
        // today's behaviour rather than to an empty option.
        titles.set(card.slot, card.display.shortTitle ?? card.display.title)
      }
    }
  }
  return TREND_SLOTS.filter((slot) => titles.has(slot)).map((slot) => ({
    slot,
    title: titles.get(slot) as string,
  }))
}

/* ── geometry: positioning only ───────────────────────────────────────────── */

export const TREND_GEOMETRY = {
  width: 640,
  height: 200,
  padLeft: 14,
  padRight: 14,
  padTop: 16,
  padBottom: 16,
  radius: 8,
} as const

const round = (n: number) => Math.round(n * 100) / 100

export function plotX(x01: number): number {
  const { width, padLeft, padRight } = TREND_GEOMETRY
  return round(padLeft + x01 * (width - padLeft - padRight))
}

/** Inverted, because SVG y grows downwards. Clamped only for the DRAWN point. */
export function plotY(y01: number): number {
  const { height, padTop, padBottom } = TREND_GEOMETRY
  const clamped = Math.min(1, Math.max(0, y01))
  return round(height - padBottom - clamped * (height - padTop - padBottom))
}

export interface TrendMarker {
  /** Series identity, surviving greyscale. */
  shape: 'circle' | 'diamond'
  /** Confidence, surviving greyscale. */
  fill: 'solid' | 'hollow'
  dashed: boolean
  radius: number
}

/**
 * Four combinations, four distinct descriptors, and **no colour in the tuple**.
 *
 * §7.2 wants non-colour encoding for every series and criterion 13 wants the
 * chart legible without colour, and those are two obligations rather than one:
 * which series a mark belongs to, and how much to trust it. Shape carries the
 * first — circle against diamond is the pair that stays distinguishable at 8px,
 * where a square reads as a circle and a triangle as a diamond. Fill carries the
 * second, with a dash as the belt to that brace, because a hollow mark on a
 * gradient card can read as filled on a low-DPI screen and a dashed outline
 * cannot. Colour rides along as a redundant third channel and is nobody's only
 * cue. Opacity was rejected: it is luminance alone, and it composites to
 * something `tokens.test.ts` cannot measure.
 */
export function markerFor(condition: Condition, lowConfidence: boolean): TrendMarker {
  return {
    shape: condition === 'EC' ? 'circle' : 'diamond',
    fill: lowConfidence ? 'hollow' : 'solid',
    dashed: lowConfidence,
    radius: lowConfidence ? TREND_GEOMETRY.radius - 1 : TREND_GEOMETRY.radius,
  }
}

/** One `<path>` for both shapes, so a test can compare `d` attributes directly. */
export function markerPath(shape: TrendMarker['shape'], cx: number, cy: number, r: number): string {
  const [x, y, rad] = [round(cx), round(cy), round(r)]
  if (shape === 'diamond') {
    return `M ${x} ${y - rad} L ${x + rad} ${y} L ${x} ${y + rad} L ${x - rad} ${y} Z`
  }
  return `M ${x - rad} ${y} a ${rad} ${rad} 0 1 0 ${rad * 2} 0 a ${rad} ${rad} 0 1 0 ${-rad * 2} 0 Z`
}

/**
 * The polyline for ONE series.
 *
 * It takes a `TrendSeries` and not a list of points, which is the whole of
 * §4.3's "never connect across a condition boundary": there is no argument shape
 * that could express a line spanning two conditions — or, since a series is one
 * headband's too, two headbands — so the rule is not a check that could be
 * forgotten. It is why the chart draws one card per series.
 */
export function linePoints(series: TrendSeries): string {
  return series.points.map((p) => `${plotX(p.x)},${plotY(p.y)}`).join(' ')
}
